import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTables } from '../lib/realtime';
import type { CompanyCompliance, Company, Driver, DrivingSession } from '../lib/types';

const monthStart = () => {
  const d = new Date();
  d.setDate(1); d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const today = () => new Date().toISOString().slice(0, 10);

function complianceStatus(todayMinutes: number, maxBreak: number) {
  if (todayMinutes > 780) return 'violation';
  if (todayMinutes > 360 && maxBreak < 30) return 'warning';
  return 'ok';
}

export function useGovStats() {
  const [companies, setCompanies] = useState<CompanyCompliance[]>([]);
  const [fleetStats, setFleetStats] = useState({
    totalCompanies: 0,
    totalActiveDrivers: 0,
    fleetComplianceRate: 0,
    totalIncomeMonth: 0,
  });
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    const [companiesRes, driversRes, sessionsRes, tripsRes] = await Promise.all([
      supabase.from('companies').select('*').eq('status', 'active'),
      supabase.from('drivers').select('id,status,company_id,user_id'),
      supabase.from('driving_sessions')
        .select('*, breaks(*)')
        .gte('started_at', today() + 'T00:00:00'),
      supabase.from('trips')
        .select('company_id,fare')
        .eq('status', 'completed')
        .gte('created_at', monthStart()),
    ]);

    const allCompanies = (companiesRes.data ?? []) as Company[];
    const allDrivers = (driversRes.data ?? []) as Driver[];
    const allSessions = (sessionsRes.data ?? []) as DrivingSession[];
    const allTrips = tripsRes.data ?? [];

    const result: CompanyCompliance[] = allCompanies.map(company => {
      const companyDrivers = allDrivers.filter(d => d.company_id === company.id);
      const activeDrivers = companyDrivers.filter(d => d.status === 'active').length;

      let okCount = 0;
      let violationCount = 0;
      const driverIds = new Set(companyDrivers.map(d => d.id));
      const driverSessions: Record<string, DrivingSession[]> = {};
      for (const s of allSessions) {
        if (driverIds.has(s.driver_id)) {
          if (!driverSessions[s.driver_id]) driverSessions[s.driver_id] = [];
          driverSessions[s.driver_id].push(s);
        }
      }
      for (const [, sessions] of Object.entries(driverSessions)) {
        const todayMins = sessions.reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0);
        const maxBreak = sessions.flatMap(s => s.breaks ?? []).reduce((m, b) => {
          if (!b.ended_at) return m;
          return Math.max(m, Math.round((new Date(b.ended_at).getTime() - new Date(b.started_at).getTime()) / 60000));
        }, 0);
        const status = complianceStatus(todayMins, maxBreak);
        if (status === 'ok') okCount++;
        if (status === 'violation') violationCount++;
      }
      const totalWithSessions = Object.keys(driverSessions).length;
      const complianceRate = totalWithSessions > 0 ? Math.round((okCount / totalWithSessions) * 100) : 100;

      const totalIncomeMonth = (allTrips as Array<{ company_id: string | null; fare: number | null }>)
        .filter(t => t.company_id === company.id)
        .reduce((sum, t) => sum + (t.fare ?? 0), 0);

      const lastSession = allSessions
        .filter(s => driverIds.has(s.driver_id))
        .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0];

      return {
        company,
        totalDrivers: companyDrivers.length,
        activeDrivers,
        complianceRate,
        violationCount,
        totalIncomeMonth,
        lastActive: lastSession?.started_at ?? null,
      };
    });

    setCompanies(result);

    const totalActiveDrivers = allDrivers.filter(d => d.status === 'active').length;
    const totalIncomeMonth = (allTrips as Array<{ fare: number | null }>).reduce((sum, t) => sum + (t.fare ?? 0), 0);
    const avgCompliance = result.length > 0 ? Math.round(result.reduce((s, c) => s + c.complianceRate, 0) / result.length) : 100;

    setFleetStats({
      totalCompanies: allCompanies.length,
      totalActiveDrivers,
      fleetComplianceRate: avgCompliance,
      totalIncomeMonth,
    });

    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();
    const unsub = subscribeTables(
      ['companies', 'drivers', 'driving_sessions', 'breaks', 'trips', 'transactions'],
      refetch
    );
    return unsub;
  }, [refetch]);

  return { companies, fleetStats, loading, refetch };
}
